/**
 * Utility functions for role-based access control
 */

import { COMPANY_PRIVILEGED_ROLES, EMPLOYEE_ROLE_DISPLAY, roleType } from "../constant/constants";

/**
 * Get the current user's role from localStorage
 * @returns {string|null} The user's role or null if not found
 */
export const getCurrentUserRole = () => {
  try {
    const sdfUserData = localStorage.getItem("sdfUser");
    if (!sdfUserData) return null;
    
    const sdfUser = JSON.parse(sdfUserData);
    return sdfUser?.role || null;
  } catch (error) {
    console.error("Error getting user role:", error);
    return null;
  }
};

/**
 * Check if the current user has any of the specified roles
 * @param {string[]} allowedRoles - Array of allowed roles
 * @returns {boolean} True if user has access, false otherwise
 */
export const hasRoleAccess = (allowedRoles) => {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  
  const currentRole = getCurrentUserRole();
  return currentRole && allowedRoles.includes(currentRole);
};

/**
 * Check if the current user has a specific role
 * @param {string} role - The role to check
 * @returns {boolean} True if user has the role, false otherwise
 */
export const hasRole = (role) => {
  const currentRole = getCurrentUserRole();
  return currentRole === role;
};

/**
 * @param {string} role
 * @param {object} [companyUser] When provided for a privileged role, also requires SETA import data.
 */
export const isCompanyPrivileged = (role, companyUser) => {
  if (!role || !COMPANY_PRIVILEGED_ROLES.includes(role)) return false;
  if (companyUser !== undefined) {
    return companyUser?.data != null;
  }
  return true;
};

export const isCompanyAdmin = (role) => role === roleType.COMPANY_ADMIN;

/** Role column display config for the employee list ({ label, className } or null). */
export const getEmployeeListRoleDisplay = (role) =>
  EMPLOYEE_ROLE_DISPLAY[role] ?? null;

export const hasCompanyPrivilegedAccess = (role) =>
  isCompanyPrivileged(role ?? getCurrentUserRole());

/**
 * Get Supabase user data safely from localStorage
 * @returns {object|null} Supabase user data or null if not found
 */
export const getSupabaseUserData = () => {
  try {
    const userData = localStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error("Error getting Supabase user data:", error);
    return null;
  }
};

/**
 * Get SDF user data safely from localStorage
 * @returns {object|null} SDF user data or null if not found
 */
export const getSdfUserData = () => {
  try {
    const sdfUserData = localStorage.getItem("sdfUser");
    return sdfUserData ? JSON.parse(sdfUserData) : null;
  } catch (error) {
    console.error("Error getting SDF user data:", error);
    return null;
  }
};

/** Persist sdfUser to localStorage and notify listeners (e.g. Sidebar). */
export const setSdfUserData = (sdfUser) => {
  localStorage.setItem("sdfUser", JSON.stringify(sdfUser));
  window.dispatchEvent(new Event("sdfUserUpdated"));
};

/**
 * Get the current user's display name
 * @returns {string} User's display name or empty string if not found
 */
export const getCurrentUserName = () => {
  try {
    const sdfUser = getSdfUserData();
    if (!sdfUser) return "";
    
    return sdfUser.first_name || sdfUser.data?.Name || "";
  } catch (error) {
    console.error("Error getting user name:", error);
    return "";
  }
}; 